import { forwardRef, memo, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretLeft, faCaretRight, faDisplay, faMaximize } from '@fortawesome/free-solid-svg-icons';
import { ErrorBoundary } from "react-error-boundary";
import Tooltip from '@mui/material/Tooltip';
import { FloatingWindowDimensions } from '../../../components/sitebase/Base.types';
import Dropdown from '../../../components/common/Dropdown';
import { FeatureWindowPresentation } from '../Map.types';
import FeatureInfoPresentationSelector from './FeatureInfoPresentationSelector';

import 'react-resizable/css/styles.css';
import '../css/FeatureInfo.css';

const FLOATING_WINDOW_MIN_WIDTH = 385;
const FLOATING_WINDOW_CLASS = 'efas-floatingwindow';

/** The draggable div carries the last touch position between touch events. */
interface DraggableElement extends HTMLDivElement {
    _lastTouch?: { x: number; y: number } | null;
}

function Clickable(props: { className: string, onMove: (e: MouseEvent) => void, onMoveEnd: () => void }) {

    const moveEvent = (e: MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        props.onMove(e);
    };

    const clear = () => {
        window.removeEventListener('mousemove', moveEvent);
        window.removeEventListener('mouseup', clear);
        props.onMoveEnd();
    };

    return <span className={props.className} style={{ userSelect: 'none' }}
        onMouseDown={() => {
            window.addEventListener('mousemove', moveEvent);
            window.addEventListener('mouseup', clear);
        }}
        onMouseUp={() => {
            window.removeEventListener('mousemove', moveEvent);
        }}

    ></span >
}

const Draggable = memo(function (props: {
    id?: string,
    'data-testid'?: string,
    children: ReactNode,
    position: { x: number, y: number },
    onDimensionChange?: (dimensions: FloatingWindowDimensions) => void,
    extraActions?: ReactNode,
    title?: string
}) {
    const draggableRef = useRef<HTMLDivElement>(null);
    const { onDimensionChange } = props;

    // unfortunately I couldn't find a way to implement this purely with React events,
    // due to the mouseMove stopping to fire once the cursor leaves the targetted element
    // and thus causing the dragging state to reset
    const moveWidget = useCallback((e: MouseEvent | TouchEvent) => {
        // Clamp position within screen bounds
        const clampPosition = (top: number, left: number, rect: DOMRect) => {
            const minTop = 0;
            const maxTop = window.innerHeight - 50; // Keep at least 50px visible vertically
            const minLeft = -rect.width + 50; // Keep at least 50px visible on the left
            const maxLeft = window.innerWidth - 50; // Keep at least 50px visible on the right
            return {
                top: Math.max(minTop, Math.min(maxTop, top)),
                left: Math.max(minLeft, Math.min(maxLeft, left)),
            };
        };

        if ('buttons' in e && e.buttons === 1) {
            if (draggableRef.current) {
                const rect = draggableRef.current!.getBoundingClientRect();
                const newTop = rect.top + e.movementY;
                const newLeft = rect.left + e.movementX;
                const clamped = clampPosition(newTop, newLeft, rect);
                draggableRef.current!.style.top = clamped.top + 'px';
                draggableRef.current!.style.left = clamped.left + 'px';
            }
        } else if ('touches' in e && e.touches.length === 1) {
            if (draggableRef.current) {
                const element = draggableRef.current as DraggableElement;
                // Track previous touch position on the draggableRef
                if (!element._lastTouch) {
                    element._lastTouch = { x: e.touches[0].clientX, y: e.touches[0].clientY };
                }
                const lastTouch = element._lastTouch;
                const deltaX = e.touches[0].clientX - lastTouch.x;
                const deltaY = e.touches[0].clientY - lastTouch.y;
                const rect = draggableRef.current!.getBoundingClientRect();
                const newTop = rect.top + deltaY;
                const newLeft = rect.left + deltaX;
                const clamped = clampPosition(newTop, newLeft, rect);
                draggableRef.current!.style.top = clamped.top + 'px';
                draggableRef.current!.style.left = clamped.left + 'px';
                element._lastTouch = { x: e.touches[0].clientX, y: e.touches[0].clientY };
            }
        }
        e.stopPropagation();
    }, []);

    const onMoveEnd = useCallback(() => {
        document.body.style.userSelect = '';
        if (draggableRef.current) {
            const rect = draggableRef.current.getBoundingClientRect();
            let top = parseInt(draggableRef.current.style.top);
            let left = parseInt(draggableRef.current.style.left);

            // Snap back within bounds if somehow out of bounds
            const minTop = 0;
            const maxTop = window.innerHeight - 50;
            const minLeft = -rect.width + 50;
            const maxLeft = window.innerWidth - 50;

            top = Math.max(minTop, Math.min(maxTop, top));
            left = Math.max(minLeft, Math.min(maxLeft, left));

            draggableRef.current.style.top = top + 'px';
            draggableRef.current.style.left = left + 'px';

            if (onDimensionChange) onDimensionChange({
                startX: left,
                startY: top,
            });
        }
        (draggableRef.current as DraggableElement)._lastTouch = null;
    }, [onDimensionChange]);

    const mouseLeaveTimeout = useRef<NodeJS.Timeout>(null);

    const handleZIndexOnAllWindows = () => {
        // if the current already has floating-window-active class, do nothing
        if (draggableRef.current?.classList.contains('floating-window-active')) {
            return;
        }

        // otherwise iterate over all floating window (query select efas-floatingwindow)
        // and remove the class 'floating-window-active'
        const windows = document.querySelectorAll(`.${FLOATING_WINDOW_CLASS}`);
        windows.forEach((win) => {
            win.classList.remove('floating-window-active');
        });

        // then add the class to the current window to make it be on top
        if (draggableRef.current) {
            draggableRef.current.classList.add('floating-window-active');
        }

    };

    return <div
        id={props.id}
        data-testid={props['data-testid']}
        ref={draggableRef}
        className={FLOATING_WINDOW_CLASS}
        style={{ position: 'fixed', top: props.position.y, left: props.position.x, border: "1px solid darkgrey", borderRadius: "5px" }}
        onMouseDown={() => {
            handleZIndexOnAllWindows();
        }}
    >
        <div className='d-flex flex-row' style={{ backgroundColor: "rgb(250, 167, 63)" }}>
            <div
                className="handle d-flex align-items-center flex-grow-1"
                onTouchStart={() => {
                    handleZIndexOnAllWindows();
                    document.body.style.userSelect = 'none';
                }}
                onTouchMove={(e) => {
                    moveWidget(e as unknown as TouchEvent);
                }}
                onTouchEnd={() => {
                    onMoveEnd();
                }}
                onMouseDown={() => {
                    document.body.style.userSelect = 'none';
                    window.addEventListener('mousemove', moveWidget);
                }}
                onMouseUp={() => {
                    window.removeEventListener('mousemove', moveWidget);
                    onMoveEnd();
                }}
                onMouseEnter={() => {
                    if (mouseLeaveTimeout.current)
                        clearTimeout(mouseLeaveTimeout.current);
                }}
                onMouseLeave={() => {
                    mouseLeaveTimeout.current = setTimeout(() => {
                        document.body.style.userSelect = '';
                        window.removeEventListener('mousemove', moveWidget);
                    }, 2000);
                }}
            >
                {props.title && <span className='text-light ms-2 fs-7 text-truncate'>{props.title}</span>}

            </div>
            <div className='d-flex flex-row ms-auto user-select-none'>
                {props.extraActions}
            </div>
        </div>
        {props.children}
    </div>;
});

/**
 * FloatingWindow component that can be used to display content in a draggable and resizable window.
 *
 * @param {Object} props - The properties for the FloatingWindow component.
 * @param {boolean} props.visible - Determines if the window is visible.
 * @param {React.ReactNode} [props.extraActions] - Additional actions to be displayed in the window header.
 * @param {React.ReactNode} [props.children] - The content to be displayed inside the window.
 * @param {number} [props.startX=0.005] - The initial horizontal position of the window as a fraction of the viewport width.
 * @param {number} [props.startY=0.12] - The initial vertical position of the window as a fraction of the viewport height.
 * @param {number} [props.width=window.innerWidth / 2.2] - The initial width of the window.
 * @param {number} [props.height=window.innerHeight / 1.5] - The initial height of the window.
 * @param {string} [props.className] - Additional CSS classes to apply to the window.
 * @param {React.ForwardedRef<HTMLDivElement>} draggable - A ref to the draggable element.
 * @returns {JSX.Element} The rendered FloatingWindow component.
 */
export default memo(forwardRef(function FloatingWindow(props: {
    id: string,
    'data-testid'?: string,
    visible: boolean,
    extraActions?: ReactNode,
    children?: ReactNode,
    startX?: number,
    startY?: number,
    width?: number,
    height?: number,
    className?: string,
    containerClassName?: string,
    title?: string,
    onDimensionChange?: (dimensions: FloatingWindowDimensions) => void,
    currentPresentation?: FeatureWindowPresentation,
    onPresentationChange?: (presentation: FeatureWindowPresentation) => void
}, draggable: React.ForwardedRef<HTMLDivElement>) {
    const smallWidth = window.outerWidth < 1024;
    const startX = smallWidth ? 10 : props.startX ? props.startX : window.innerWidth * 0.005;
    const startY = smallWidth ? 110 : props.startY ? props.startY : window.innerHeight * 0.12;

    const [width, setWidth] = useState<number>(smallWidth ? window.innerWidth * 0.95 : props.width ? props.width : window.innerWidth * 0.95);
    const [height, setHeight] = useState<number>(smallWidth ? window.innerHeight * 0.8 : props.height ? props.height : window.innerHeight / 1.25);
    const [pos, setPos] = useState({ x: startX, y: startY });

    const resizableBox = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Focus the window when it becomes visible
    useEffect(() => {
        if (props.visible && containerRef.current) {
            const floatingWindow = containerRef.current.querySelector<HTMLElement>(`.${FLOATING_WINDOW_CLASS}`);
            if (floatingWindow) {
                // Bring to front
                document.querySelectorAll(`.${FLOATING_WINDOW_CLASS}`).forEach((win) => {
                    win.classList.remove('floating-window-active');
                });
                floatingWindow.classList.add('floating-window-active');
                floatingWindow.focus();
            }
        }
    }, [props.visible]);

    const updateDimensions = (dimensions: FloatingWindowDimensions) => {
        const currentWidth = parseInt(resizableBox.current!.style.width.substring(0, resizableBox.current!.style.width.length - 2));
        const currentHeight = parseInt(resizableBox.current!.style.height.substring(0, resizableBox.current!.style.height.length - 2));
        if (props.onDimensionChange) props.onDimensionChange({
            startX: dimensions.startX || pos.x,
            startY: dimensions.startY || pos.y,
            width: dimensions.width || currentWidth,
            height: dimensions.height || currentHeight,
        });
    };

    const onResizeableMoveEnd = () => {
        const currentWidth = parseInt(resizableBox.current!.style.width.substring(0, resizableBox.current!.style.width.length - 2));
        const currentHeight = parseInt(resizableBox.current!.style.height.substring(0, resizableBox.current!.style.height.length - 2));
        updateDimensions({ width: currentWidth, height: currentHeight })
    };

    return props.visible &&
        <div ref={(node) => {
            containerRef.current = node;
            if (typeof draggable === 'function') draggable(node);
            else if (draggable) draggable.current = node;
        }} className={props.containerClassName || ""}>
            <Draggable
                id={props.id}
                data-testid={props['data-testid']}
                position={pos}
                title={props.title}
                onDimensionChange={updateDimensions}
                extraActions={<>
                    {width > 620 &&
                        <>
                            {props.onPresentationChange && <Dropdown
                                toggleButton={
                                    <Tooltip title="Point Information Presentation" placement="bottom" arrow>
                                        <span className='fi-close-border'><FontAwesomeIcon icon={faDisplay} /></span>
                                    </Tooltip>
                                }
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                width="250px"
                            >
                                <FeatureInfoPresentationSelector
                                    id={props.id}
                                    currentPresentation={props.currentPresentation!}
                                    onPresentationChange={props.onPresentationChange}
                                />
                            </Dropdown>}
                            <Tooltip title="Align window left" placement="bottom" arrow>
                                <span className='fi-close'
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                    }}
                                    onMouseUp={(e) => {
                                        const width = window.innerWidth * 0.45;
                                        const height = window.innerHeight * 0.8;
                                        setWidth(width);
                                        setHeight(height);
                                        setPos({ x: 10, y: 110 });
                                        updateDimensions({ startX: 10, startY: 110, width: width, height: height });
                                        e.stopPropagation();
                                        e.preventDefault();
                                    }}><FontAwesomeIcon icon={faCaretLeft} /></span>
                            </Tooltip>
                            {/* <Tooltip title="Align window bottom" placement="bottom" arrow>
                                <span className='fi-close'
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                    }}
                                    onMouseUp={(e) => {
                                        const width = window.innerWidth * 0.99
                                        const height = window.innerHeight * 0.39
                                        setWidth(width);
                                        setHeight(height);
                                        const y = window.innerHeight * 0.55;
                                        setPos({ x: 10, y: y });
                                        updateDimensions({ startX: 10, startY: y, width: width, height: height });
                                        e.stopPropagation();
                                        e.preventDefault();
                                    }}><FontAwesomeIcon icon={faCaretDown} /></span>
                            </Tooltip> */}
                            <Tooltip title="Align window right" placement="bottom" arrow>
                                <span className='fi-close'
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                    }}
                                    onMouseUp={(e) => {
                                        const width = window.innerWidth * 0.45;
                                        const height = window.innerHeight * 0.8;
                                        setWidth(width);
                                        setHeight(height);
                                        setPos({ x: width, y: 110 });
                                        updateDimensions({ startX: 10, startY: 110, width: width, height: height });
                                        e.stopPropagation();
                                        e.preventDefault();
                                    }}><FontAwesomeIcon icon={faCaretRight} /></span>
                            </Tooltip></>}
                    <Tooltip title="Maximise window" placement="bottom" arrow disableInteractive>
                        <span className='fi-close' onMouseUp={(e) => {
                            const width = window.innerWidth * 0.95;
                            const height = window.innerHeight * 0.8;
                            setWidth(width);
                            setHeight(height);
                            setPos({ x: 10, y: 110 });
                            updateDimensions({ startX: 10, startY: 110, width: width, height: height });
                            e.stopPropagation();
                            e.preventDefault();
                        }}><FontAwesomeIcon icon={faMaximize} /></span>
                    </Tooltip>
                    {props.extraActions}
                </>}>
                <div ref={resizableBox} style={{ width: width, height: height, backgroundColor: "white", boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.3)", position: "relative" }}>
                    <ErrorBoundary fallback={<div className='p-2'>An error occurred while rendering this content.</div>}>
                        <div className={`floating-content ${props.className || ""}`}>
                            {props.children}
                        </div>
                    </ErrorBoundary>
                    <Clickable className="react-resizable-handle react-resizable-handle-e"
                        onMove={(e) => {
                            if (e.stopPropagation) e.stopPropagation();
                            if (e.preventDefault) e.preventDefault();

                            const currentWidth = parseInt(resizableBox.current!.style.width.substring(0, resizableBox.current!.style.width.length - 2));
                            const newWidth = Math.max(FLOATING_WINDOW_MIN_WIDTH, currentWidth + e.movementX);
                            resizableBox.current!.style.width = newWidth + 'px';
                            setWidth(newWidth);
                        }}
                        onMoveEnd={onResizeableMoveEnd} />
                    <Clickable className="react-resizable-handle react-resizable-handle-s"
                        onMove={(e) => {
                            if (e.stopPropagation) e.stopPropagation();
                            if (e.preventDefault) e.preventDefault();

                            const currentHeight = parseInt(resizableBox.current!.style.height.substring(0, resizableBox.current!.style.height.length - 2));
                            const newHeight = currentHeight + e.movementY;
                            resizableBox.current!.style.height = newHeight + 'px';
                            setHeight(newHeight);
                        }}
                        onMoveEnd={onResizeableMoveEnd} />

                    <Clickable className="react-resizable-handle react-resizable-handle-se"
                        onMove={(e) => {
                            if (e.stopPropagation) e.stopPropagation();
                            if (e.preventDefault) e.preventDefault();

                            const currentWidth = parseInt(resizableBox.current!.style.width.substring(0, resizableBox.current!.style.width.length - 2));
                            const newWidth = Math.max(FLOATING_WINDOW_MIN_WIDTH, currentWidth + e.movementX);
                            resizableBox.current!.style.width = newWidth + 'px';
                            const currentHeight = parseInt(resizableBox.current!.style.height.substring(0, resizableBox.current!.style.height.length - 2));
                            const newHeight = currentHeight + e.movementY;
                            resizableBox.current!.style.height = newHeight + 'px';
                            setWidth(newWidth);
                            setHeight(newHeight);
                        }}
                        onMoveEnd={onResizeableMoveEnd} />
                </div>
            </Draggable>
        </div>;
}));